import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useImportPayrollBatch } from "@/features/payroll/api/use-import-payroll-batch";
import type { PayrollBatchImportFormData } from "@/features/payroll/api/dto";
import { PAYROLL_FEE_PAYERS } from "@/features/payroll/api/enums";
import {
  createImportPayrollBatchSchema,
  type ImportPayrollBatchFormValues,
} from "@/features/payroll/schemas/payroll-batch.schema";

const EMPTY_VALUES: Omit<ImportPayrollBatchFormValues, "file"> & {
  file: File | null;
} = {
  file: null,
  label: "",
  feePayer: "SENDER",
};

/**
 * Import d'une paie depuis un classeur Excel (`POST /PayrollBatches/import`,
 * multipart). Modèle attendu : feuille « Paie » (Nom complet, Téléphone,
 * Montant GNF, Matricule). Récupérable via le bouton « Télécharger le modèle »
 * de la page liste. Preview `MutationConfirmPreview` (pas d'argent en jeu avant
 * simulate+launch).
 */
export function PayrollBatchImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const tPayroll = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => createImportPayrollBatchSchema(), [i18n.language]);

  const importBatch = useImportPayrollBatch();
  const pending = importBatch.isPending;
  const [preview, setPreview] = useState<PayrollBatchImportFormData | null>(
    null
  );

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      if (!value.file) return;
      const parsed = schema.safeParse({
        file: value.file,
        label: value.label,
        feePayer: value.feePayer,
      });
      if (!parsed.success) return;
      setPreview({
        file: parsed.data.file,
        label: parsed.data.label,
        feePayer: parsed.data.feePayer,
      });
    },
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("payroll.batches.import.title")}</DialogTitle>
          </DialogHeader>

          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="file">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="payroll-import-file">
                    {t("payroll.batches.import.file")}
                  </Label>
                  <Input
                    id="payroll-import-file"
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      field.handleChange(file);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("payroll.batches.import.fileHint")}
                  </p>
                </div>
              )}
            </form.Field>

            <form.Field name="label">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="payroll-import-label">
                    {t("payroll.batches.form.label")}
                  </Label>
                  <Input
                    id="payroll-import-label"
                    maxLength={120}
                    placeholder={t("payroll.batches.form.labelPlaceholder")}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="feePayer">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="payroll-import-fee-payer">
                    {t("payroll.batches.form.feePayer")}
                  </Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) =>
                      field.handleChange(
                        value as ImportPayrollBatchFormValues["feePayer"]
                      )
                    }
                  >
                    <SelectTrigger id="payroll-import-fee-payer">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYROLL_FEE_PAYERS.map((value) => (
                        <SelectItem key={value} value={value}>
                          {t(`payroll.enums.feePayer.${value}` as TFunctionType)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("payroll.batches.import.submit")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={
          preview && {
            label: preview.label,
            feePayer: preview.feePayer,
            fileName: preview.file.name,
            fileSize: preview.file.size,
          }
        }
        title={t("payroll.batches.import.previewTitle")}
        description={t("payroll.batches.import.previewDescription")}
        t={tPayroll}
        pending={pending}
        onConfirm={() => {
          if (!preview) return;
          importBatch.mutate(preview, {
            onSuccess: () => {
              notifSilence(t("payroll.batches.feedback.imported"));
              setPreview(null);
              onOpenChange(false);
            },
            onError: (error) =>
              notifSilenceError(apiErrorHandler(error) || error.message),
          });
        }}
      />
    </>
  );
}
